














































































































































import Vue from 'vue';

import { cloneDeep, chunk, kebabCase, isEmpty } from 'lodash';
import { param } from 'jquery';

import { VBModal, BRow, BCol } from 'bootstrap-vue';

import LoadingSpinner from '@reciprocity/loading-spinner';
import ZenButton from '@reciprocity/button';
import { postRequest, getRequest } from '../../utils/client/client';
import { Alert } from '@/components/global_alerts/types';
import { buildURL } from './mapper_utils';
import { Item, MappedItem, ObjectType, Filter } from './mapper_types';

import SearchableList from './SearchableList.vue';
import ColumnTitle from './ColumnTitle.vue';
import SelectItem from './list_items/SelectItem.vue';
import MappingItem from './list_items/MappingItem.vue';

export interface ObjectModel {
  model: string;
  plural: string;
  singular: string;
}

export default Vue.extend({
  name: 'ObjectMapper',
  components: {
    BRow,
    BCol,
    SearchableList,
    SelectItem,
    MappingItem,
    ColumnTitle,
    LoadingSpinner,
    ZenButton,
  },
  directives: {
    bModal: VBModal,
  },
  props: {
    leftColumnType: {
      type: Object as () => ObjectModel,
      default: () => ({
        singular: '',
        plural: '',
        model: '',
      }),
    },
    leftColumnObjects: {
      type: Array,
      default: () => [],
    },
    availableMapTypes: {
      type: Object,
      default: () => {},
    },
    rightColumnType: {
      type: Object as () => ObjectModel,
      default: () => ({
        singular: '',
        plural: '',
        model: '',
      }),
    },
    csrfToken: {
      type: String,
      required: true,
    },
    rightColumnObjectsApi: {
      type: String,
      required: true,
    },
    relatedObjectsApi: {
      type: String,
      required: true,
    },
    startingFilters: {
      type: Array as () => Filter[],
      default: () => [{ object: null, type: null }],
    },
    extraRequestData: {
      type: Object,
      default: () => {},
    },
    extraPostData: {
      type: Object,
      default: () => {},
    },
    rightColumnObjects: {
      type: Array,
      default: () => [],
    },
    mapWithoutSaving: {
      type: Boolean,
      default: false,
    },
    hideFilters: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Set size of request batches when mapping
      BATCH_SIZE: 25,
      rightColumnObjectType: <ObjectType>{},
      leftColumnSelectedItem: <Item>this.leftColumnObjects[0] || {},
      unmappedObjects: <Array<MappedItem>>[],
      mappedObjects: <Array<MappedItem>>[],
      rightLoadingState: <boolean>false,
      middleLoadingState: <boolean>false,
      isMapping: <boolean>false,
      filters: <Array<Filter>>this.startingFilters,
      searchProps: ['title', 'description'],
      userAction: {
        type: '',
        done: 0,
        all: 0,
      },
    };
  },
  computed: {
    filterActiveClass(): string {
      return this.filters[0].object ? 'outline-primary' : 'outline-secondary';
    },
    mappingSpinnerCopy(): string {
      return `${this.userAction.type} ${this.userAction.done} of ` + `${this.userAction.all}`;
    },
    leftTitle(): string {
      var title = 'Objects';
      if (this.leftColumnType.plural) {
        title = this.leftColumnType.plural;
      }
      return 'Available ' + title;
    },
    middleTitle(): string {
      return this.rightColumnObjectType.plural + ' mapped';
    },
    rightTitle(): string {
      return 'Available ' + this.rightColumnObjectType.plural;
    },
  },
  created() {
    if (this.rightColumnType.model) {
      this.rightColumnObjectType = this.rightColumnType;
      this.rightLoadingState = true;
      this.middleLoadingState = true;
      this.setRightColumnObjects();
      this.setMiddleColumnObjects();
    }

    if (!this.$store) {
      window.GGRC.zenModal.$on('changeType', this.changeRightColumnType);
    }
  },
  methods: {
    selectLeftColumnItem(item: Item): void {
      this.leftColumnSelectedItem = item;
      if (this.rightColumnObjectType.model) {
        this.changeRightColumnType(this.rightColumnObjectType, this.filters);
      }
    },
    updateObjectState(items: MappedItem[], mapped: boolean): any {
      // Change local state
      items.map((i: MappedItem) => {
        i.mapped = mapped;

        if (mapped) {
          this.mappedObjects.push(i);
          const index = this.unmappedObjects.indexOf(i);
          if (index > -1) {
            this.unmappedObjects.splice(index, 1);
          }
        } else {
          this.unmappedObjects.push(i);
          const index = this.mappedObjects.indexOf(i);
          if (index > -1) {
            this.mappedObjects.splice(index, 1);
          }
        }
      });

      this.mappedObjects.sort((a: MappedItem, b: MappedItem) => a.title.localeCompare(b.title));
      this.unmappedObjects.sort((a: MappedItem, b: MappedItem) => a.title.localeCompare(b.title));
      this.$emit('mapping-items', { mapped: this.mappedObjects, unmapped: this.unmappedObjects });
    },
    unmapItems(selectedItems: MappedItem[]) {
      this.userAction.type = 'Unmapping';
      this.userAction.all = selectedItems.length;
      this.changeObjectState(selectedItems, false);
    },
    mapItems(selectedItems: MappedItem[]) {
      this.userAction.type = 'Mapping';
      this.userAction.all = selectedItems.length;
      this.changeObjectState(selectedItems, true);
    },
    async changeObjectState(items: MappedItem[], mapped: boolean) {
      if (this.mapWithoutSaving) {
        this.updateObjectState(items, mapped);
        this.userAction.done = true;
        return;
      }
      const batchedItems = chunk(items, this.BATCH_SIZE);
      this.isMapping = true;
      this.$emit('mapping', true);
      for await (const [idx, batch] of batchedItems.entries()) {
        this.userAction.done = Math.min((idx + 1) * this.BATCH_SIZE, this.userAction.all);
        await this.saveNewObjectState(batch, mapped)
          .then(() => {
            this.updateObjectState(batch, mapped);
          })
          .catch(resp => {
            this.showAlert({
              type: 'error',
              message: 'Failed to save object mapping',
            });
            throw resp;
          });
      }
      this.$nextTick(function() {
        if (mapped) {
          this.$refs.mappedObjectsList.scrollToFirstSelectedItem();
        } else {
          this.$refs.unmappedObjectsList.scrollToFirstSelectedItem();
        }
      });
      this.isMapping = false;
      this.$emit('mapping', false);
    },
    saveNewObjectState(items: MappedItem[], state: boolean): Promise<any> {
      return postRequest({
        url: buildURL(this.rightColumnObjectsApi, this),
        data: {
          mapping_objects: (items || []).map(item => ({
            identifier: (item || {}).identifier,
            mapped: state,
          })),
          ...this.extraPostData,
        },
        headers: {
          'X-CSRF-Token': this.csrfToken,
          'content-type': 'application/json',
        },
      });
    },
    setRightColumnObjects(): Promise<any> {
      if (isEmpty(this.leftColumnSelectedItem)) {
        let unmappedObjects = [];
        if (this.mapWithoutSaving && this.rightColumnObjects) {
          unmappedObjects = this.rightColumnObjects.filter(o => !o.mapped);
        }
        this.unmappedObjects = unmappedObjects;
        this.rightLoadingState = false;
        return Promise.resolve();
      }

      // Prepare request JSON from filters
      const related = this.prepareFilterJson();

      return getRequest({
        url: buildURL(this.rightColumnObjectsApi, this),
        params: {
          related_filters: JSON.stringify(related),
          ...this.extraRequestData,
          only: 'unmapped',
        },
        paramsSerializer: function(params) {
          return param(params);
        },
      })
        .then((data: any) => {
          this.unmappedObjects = data.mapping_objects.map((item: MappedItem) =>
            Object.assign(item, { selected: false })
          );
          this.rightLoadingState = false;
        })
        .catch(resp => {
          this.showAlert({
            type: 'error',
            message: 'Failed to load objects',
          });
          throw resp;
        });
    },
    setMiddleColumnObjects(): Promise<any> {
      if (isEmpty(this.leftColumnSelectedItem)) {
        let mappedObjects = [];
        if (this.mapWithoutSaving && this.rightColumnObjects) {
          mappedObjects = this.rightColumnObjects.filter(o => o.mapped);
        }
        this.mappedObjects = mappedObjects;
        this.middleLoadingState = false;
        return Promise.resolve();
      }

      return getRequest({
        url: buildURL(this.rightColumnObjectsApi, this),
        params: {
          ...this.extraRequestData,
          only: 'mapped',
        },
        paramsSerializer: function(params) {
          return param(params);
        },
      })
        .then((data: any) => {
          this.mappedObjects = data.mapping_objects.map((item: MappedItem) => Object.assign(item, { selected: false }));
          this.middleLoadingState = false;
        })
        .catch(resp => {
          this.showAlert({
            type: 'error',
            message: 'Failed to load objects',
          });
          throw resp;
        });
    },
    prepareFilterJson(): { type: string; id: number }[] {
      return this.filters.reduce((acc: any, filter: Filter) => {
        if (filter.type && filter.object) {
          acc.push({
            type: filter.type.model,
            id: filter.object.id,
          });
        }
        return acc;
      }, []);
    },
    changeRightColumnType(type: ObjectType, filters: Filter[]) {
      this.rightColumnObjectType = type;
      this.filters = cloneDeep(filters);
      this.rightLoadingState = true;
      this.middleLoadingState = true;

      this.setRightColumnObjects().then(() => {
        // Used to trigger URL change in Jinja templates
        this.$emit('changeType', type.model);
      });
      this.setMiddleColumnObjects();
    },
    openFilterModal() {
      const modal = {
        type: 'MapperFilterModal',
        data: {
          apiUrl: this.relatedObjectsApi,
          selectedType: this.rightColumnObjectType,
          mapTypes: this.availableMapTypes,
          pageObjectType: this.leftColumnType.model,
          filters: this.filters,
        },
      };

      if (this.$store) {
        this.$store
          .dispatch('modals/show', modal)
          .then(payload => this.changeRightColumnType(...payload))
          .catch(() => {});
      } else {
        window.GGRC.zenModal.show({
          ...modal,
          config: { ignoreUrl: true },
        });
      }
    },
    showAlert(alert: Alert) {
      if (this.$store) {
        this.$store.dispatch('alerts/show', alert);
      } else {
        window.GGRC.Notify(alert);
      }
    },
    kebabCase,
  },
});
